/**
 * Formats an ISO date string as a human-readable relative time (e.g. "2 hours ago").
 *
 * Uses `Intl.RelativeTimeFormat` so output respects the given locale.
 * Falls back to a short date (e.g. "Jan 15") for dates older than 30 days.
 *
 * @param isoDate - ISO 8601 date string
 * @param locale  - BCP 47 locale tag (default: 'en')
 * @returns Relative time string
 *
 * @example
 * formatTimeAgo('2024-01-15T10:00:00Z', 'en') // "3 days ago"
 * formatTimeAgo('2024-01-15T10:00:00Z', 'es') // "hace 3 días"
 */
export function formatTimeAgo(isoDate: string, locale = 'en'): string {
  const date = new Date(isoDate);
  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const abs = Math.abs(diffSeconds);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (abs < 60) return rtf.format(diffSeconds, 'second');

  const diffMinutes = Math.round(diffSeconds / 60);
  if (Math.abs(diffMinutes) < 60) return rtf.format(diffMinutes, 'minute');

  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 24) return rtf.format(diffHours, 'hour');

  const diffDays = Math.round(diffHours / 24);
  if (Math.abs(diffDays) < 30) return rtf.format(diffDays, 'day');

  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(date);
}
